// src/components/mdx/client/visualizations/BlogChartShell.client.tsx

/**
 * Shared card shell for interactive blog chart visualizations.
 */

'use client'

import type { ReactNode } from 'react'

import { cn } from '~/lib/utils'

/**
 * Props for BlogChartShell.
 *
 * @typedef {Object} BlogChartShellProps
 * @property {string} title - Chart heading.
 * @property {string} description - Supporting copy under the title.
 * @property {string} gradient - Tailwind gradient utility classes for the title.
 * @property {ReactNode} children - Chart body.
 * @property {string} [className] - Optional wrapper classes.
 */
export interface BlogChartShellProps {
  title: string
  description: string
  gradient: string
  children: ReactNode
  className?: string
}

/**
 * Renders the shared dark gradient card used by blog chart components.
 *
 * @param {BlogChartShellProps} props - Shell props.
 * @returns {JSX.Element} Chart card wrapper.
 */
export function BlogChartShell({
  title,
  description,
  gradient,
  children,
  className
}: BlogChartShellProps): JSX.Element {
  return (
    <div
      data-blog-chart-shell={title}
      className={cn('overflow-x-hidden rounded-xl bg-gradient-to-br from-gray-900 to-gray-950 shadow-2xl sm:rounded-3xl', className)}
    >
      <div className="p-4 sm:p-8">
        <h2 className={cn('mb-1 text-xl font-bold text-transparent sm:text-2xl bg-clip-text bg-gradient-to-r', gradient)}>
          {title}
        </h2>
        <p className="mb-4 text-sm text-gray-400 sm:mb-6 sm:text-base">{description}</p>
        {children}
      </div>
    </div>
  )
}
